import {
  ReactElement,
  useEffect,
  useMemo,
  useState,
  useContext,
  lazy,
  Suspense,
  useRef
} from "react";
import moment from "moment";
import "./CheckOut.css";
import { ReactComponent as Tick } from "../../assets/icons/round_check.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import Button from "../../UI/Button/Button";
import ButtonPlane from "../../UI/Button/ButtonPlane";
import EmptyState from "../../UI/EmptyState/EmptyState";
import { stateType } from "../../store";
import { connect } from "react-redux";
import {
  setCheckOutError,
  setDirectPayment,
  setEntireCart,
  setOrderDetails,
  setOrderInfo,
  setPageCount,
  setPaymentSuccess
} from "../../state_management/actions";
import { section_array } from "../../state_management/reducers/order";
import CheckOutHeader from "../../elements/CheckOutHeader/CheckOutHeader";
import { cartItem, extrasCartItem } from "../../state_management/reducers/cart";
import { useNavigate } from "react-router-dom";
import {
  initialState as defaultOrderDetails,
  isComplete,
  orderInfo,
  orderType,
  proceed_val_type
} from "../../state_management/reducers/order";
import { asyncResType, useAsync } from "../../utilities/asyncFunc";
import { stripEircodeFromAddress } from "../../utilities/misc";
import useQuery from "../../utilities/Query";
import { directResolve } from "./getter";
import { baseName } from "../../settings";
import { extra_service_ext, service, settings } from "../../state_management/actionTypes";
import { setTimeError } from "../../state_management/actions/common";
import { useTimeout } from "../../utilities/useTimeout";
import { serviceItems } from "../../state_management/epics/settings";
import CheckOutProvider, { ServiceDurationContext } from "./CheckOutProvider";
import { getCartTotal, only_gift } from "../../utilities/cartUtilities";
import {
  CartTotalContext,
  CouponObjectContext,
  LocaleContext
} from "../../AppProvider";
import { retrieveData } from "../../state_management/epics/settings";
import { getAllServices } from "../../state_management/epics/services";

const CheckOutExtra = lazy(
  () => import("../../elements/CheckOutExtra/CheckOutExtra")
);
const CheckOutTime = lazy(
  () => import("../../elements/CheckOutTime/CheckOutTime")
);
const CheckOutAddress = lazy(
  () => import("../../elements/CheckOutAddress/CheckOutAddress")
);
const Payment = lazy(() => import("../../elements/CheckOutPayment"));
const PaymentDirect = lazy(
  () => import("../../elements/CheckOutPayment/PaymentDirect")
);
const CheckOutLogin = lazy(
  () => import("../../elements/CheckOutLogin/CheckOutLogin")
);

const CheckOut = ({
  pageCount,
  setPageCount,
  cart,
  extras,
  orderDetails,
  setOrderDetails,
  orderDirect,
  setDirectPayment,
  setEntireCart,
  orderInfo,
  setOrderInfo,
  orderSuccess,
  setPaymentSuccess,
  setCheckOutError,
  settings,
  setTimeError,
  timeError,
  settingRes
}: {
  pageCount: proceed_val_type;
  setPageCount: (data: number) => void;
  cart: cartItem[];
  extras: extrasCartItem[];
  orderDetails: orderType;
  setOrderDetails: (data: Partial<orderType>) => void;
  orderDirect: boolean;
  setDirectPayment: (data: boolean) => void;
  setEntireCart: (cart: cartItem[], extras: extrasCartItem[]) => void;
  orderInfo: orderInfo;
  setOrderInfo: (data: orderInfo) => void;
  orderSuccess: boolean;
  setPaymentSuccess: (data: boolean) => void;
  setCheckOutError: (data: boolean) => void;
  settings: Partial<settings>;
  setTimeError: (data: boolean) => void;
  timeError: boolean;
  settingRes: asyncResType<serviceItems, settings>;
}) => {
  const query = useQuery();

  const direct = query.get("direct");
  const token = query.get("token");
  const id = query.get("id");

  const directUrl = !!(direct && token && id);

  const hasHydrated = useRef(false);

  const deactivateBars = orderSuccess || orderDirect || directUrl;
  const { t } = useContext(LocaleContext);
  const [cartTotal, setCartTotal] = useContext(CartTotalContext);
  const { CouponObject } = useContext(CouponObjectContext);

  const [__, setServiceDuration] = useContext(ServiceDurationContext);

  const duration = (() => {
    const getDuration = (duration: number) => {
      const isNum = !isNaN(parseFloat(`${duration}`));

      return isNum ? duration : 0;
    };
    const durationArray = cart.map(
      (item) => getDuration(item.item.duration) * item.quantity
    );

    const extraDurationArray = extras.map(
      (item) => getDuration(item.item.duration) * item.quantity
    );

    const durationTotal = [...durationArray, ...extraDurationArray].reduce(
      (a, b) => a + b,
      0
    );
    return durationTotal;
  })();

  useEffect(() => {
    setServiceDuration(duration);
  }, [duration]);

  const navigate = useNavigate();
  const cartCombined = [...cart, ...extras] as (cartItem | extrasCartItem)[];

  useEffect(() => {
    if (
      !cart.length && 
      !deactivateBars && 
      !hasHydrated.current && 
      !query.get("c") && 
      !query.get("e")
    ) {
      navigate(baseName || "/", { replace: true });
    }
  }, [cart.length, deactivateBars, navigate, query]);

  useEffect(() => {
    // Remove the order info if date or cleaner details changes
    const changed =
      orderInfo?.time_start !== orderDetails.time_start ||
      orderInfo?.cleaner_id !== orderDetails.cleaner_id ||
      orderInfo?.random_cleaner !== orderDetails.random_cleaner;

    if (changed && !orderInfo?.reschedule_token) {
      setOrderInfo(null);
    }
  }, [
    orderDetails.time_start,
    orderDetails.cleaner_id,
    orderDetails.random_cleaner,
    JSON.stringify(cartCombined)
  ]);

  const directRes = useAsync(
    {
      asyncFunc: directResolve,
      funcParams: { direct, orderId: id, token },
      immediate: true,
      clearOnError: true
    },
    [direct, id, token]
  );

  useEffect(() => {
    const detailRes = directRes.data;
    setDirectPayment(!!detailRes);
  }, [directRes.data]);

  useEffect(() => {
    return () => {
      setDefault();
    };
  }, []);

  useTimeout(
    {
      func: () => setTimeError(false),
      time: 5000,
      call: timeError
    },
    []
  );

  useEffect(() => {
    if (timeError) {
      setTimeError(false); // Remove error display on every date change that succeeds a timeError
    }
  }, [orderDetails.time_start]);

  useEffect(() => {
    setCheckOutError(false);
  }, [pageCount]);

  useEffect(() => {
    if (orderSuccess) {
      CouponObject.reset();
    }
  }, [orderSuccess]);

  const setDefault = () => {
    setPaymentSuccess(false);
    setDirectPayment(false);
    setCheckOutError(false);
    setOrderDetails(
      orderSuccess
        ? { ...defaultOrderDetails.details }
        : {
            time_start: null,
            cleaner_id: null,
            random_cleaner: true
          }
    );
    setOrderInfo(null);
    setTimeError(false);
    setPageCount(0);
  };

  /* Total after discount */
  const total = useMemo(() => {
    if (orderDirect) {
      return getCartTotal({
        orderDirect: true,
        cart: directRes?.data?.data.services || [],
        extras: directRes?.data?.data.extra_services || [],
        gift_values: directRes?.data?.data.gift_values || []
      });
    } else {
      return cartTotal;
    }
  }, [cartCombined, orderDirect]);

  useEffect(() => {
    setCartTotal(total);
  }, [total]);

  const onlyGift = only_gift({ cart, extras });
  useEffect(() => {
    // If it is not in the first page
    if (pageCount > 0) {
      const previousPageNumber = pageCount - 1;

      // If the previous page is not complete, go back to it
      const proceed = isComplete(
        (pageCount - 1) as proceed_val_type,
        orderDetails,
        deactivateBars,
        onlyGift
      );
      if (!proceed) {
        setPageCount(previousPageNumber);
      }
    }
  }, [pageCount]);

  const hasSettings =
    settings.order_creation_before_interval !== undefined &&
    settings.order_reschedule_interval !== undefined &&
    settings.minimum_order !== undefined;

  const customer_email = orderDetails.client_email;

  useEffect(() => {
    const cStr = query.get("c");
    const eStr = query.get("e");

    if ((cStr || eStr) && !hasHydrated.current) {
      hasHydrated.current = true;
      
      const hydrateFromUrl = async () => {
        let allServices = retrieveData("all_services") as service[] | null;
        if (!allServices || allServices.length === 0) {
          allServices = await getAllServices();
        }
        if (!allServices) allServices = [];

        const newCart: cartItem[] = [];
        const newExtras: extrasCartItem[] = [];

        if (cStr) {
          const cItems = cStr.split(',');
          cItems.forEach(itemStr => {
            const [idStr, qtyStr, upsellStr] = itemStr.split('-');
            const id = parseInt(idStr);
            const quantity = parseInt(qtyStr);
            const is_upsell = upsellStr === '1';

            const serviceItem = allServices!.find(s => s.id === id);
            if (serviceItem && !isNaN(quantity) && quantity > 0) {
              newCart.push({
                id: serviceItem.id,
                item: { ...serviceItem, extra: false, parent_id: undefined },
                quantity,
                parent_id: undefined,
                is_upsell,
                type: serviceItem.type === "gift" ? "gift" : "typical"
              });
            }
          });
        }

        if (eStr) {
          const eItems = eStr.split(',');
          eItems.forEach(itemStr => {
            const [idStr, qtyStr, parentIdStr] = itemStr.split('-');
            const id = parseInt(idStr);
            const quantity = parseInt(qtyStr);
            const parent_id = parseInt(parentIdStr);

            const parentService = allServices!.find(ps => ps.id === parent_id);
            if (parentService) {
              const fullExtra = parentService.extra_services.find(e => e.id === id);
              if (fullExtra && !isNaN(quantity) && quantity > 0) {
                newExtras.push({
                  id: fullExtra.id,
                  item: { 
                    ...fullExtra, 
                    parent_id, 
                    extra: true,
                    duration: fullExtra.duration,
                    price: fullExtra.price,
                    discount: fullExtra.discount,
                    price_with_discount: fullExtra.price - (fullExtra.discount || 0)
                  },
                  quantity,
                  parent_id,
                  type: "extra"
                });
              }
            }
          });
        }

        setEntireCart(newCart, newExtras);
        setPageCount(section_array[2]);

        const address = query.get("address");
        const lat = query.get("lat");
        const lng = query.get("lng");
        const eircode = query.get("eircode");
        const time_start = query.get("time_start");
        const cleaner_id = query.get("cleaner_id");
        const random_cleaner = query.get("random_cleaner");

        if (address || time_start || cleaner_id) {
          const updatedOrderDetails: Partial<orderType> = {};
          let finalAddress = stripEircodeFromAddress(address, eircode);
          if (finalAddress) updatedOrderDetails.client_address = finalAddress;
          if (lat) updatedOrderDetails.latitude = parseFloat(lat);
          if (lng) updatedOrderDetails.longitude = parseFloat(lng);
          if (eircode) updatedOrderDetails.eircode = eircode;
          if (time_start) updatedOrderDetails.time_start = time_start;
          if (cleaner_id) updatedOrderDetails.cleaner_id = parseInt(cleaner_id);
          if (random_cleaner === "false") updatedOrderDetails.random_cleaner = false;
          
          setOrderDetails(updatedOrderDetails);
        }
      };
      
      hydrateFromUrl();
    }
  }, [query.get("c"), query.get("e")]);

  useEffect(() => {
    if (directUrl && directRes.data && !hasHydrated.current) {
      hasHydrated.current = true;

      const hydrateCart = async () => {
        const orderData = directRes.data!;

        // Use cached services, or fetch from API if cache is empty (e.g. fresh incognito)
        let allServices = retrieveData("all_services") as service[] | null;
        if (!allServices || allServices.length === 0) {
          allServices = await getAllServices();
        }
        if (!allServices) allServices = [];

        const newCart: cartItem[] = [];
        const newExtras: extrasCartItem[] = [];

        let orderTotalDuration = 0;
        if (orderData.data.time_start && orderData.data.time_end) {
           const start = moment(orderData.data.time_start);
           const end = moment(orderData.data.time_end);
           if (start.isValid() && end.isValid()) {
             orderTotalDuration = end.diff(start, 'minutes');
           }
        }
        
        let sumStandardDurations = 0;
        
        if (orderData.data.services) {
          orderData.data.services.forEach((orderItem) => {
            const serviceItem = allServices!.find((sItem) => sItem.id === orderItem.id)
            if (serviceItem) {
               let d = orderItem.duration !== undefined ? orderItem.duration : serviceItem.duration;
               sumStandardDurations += d * orderItem.quantity;
            }
          });
        }
        
        if (orderData.data.extra_services) {
          orderData.data.extra_services.forEach((extraItem) => {
            const parentService = allServices!.find(psItem => psItem.id === extraItem.parent_id);
            if (parentService) {
              const fullExtra = parentService.extra_services.find(e => e.id === extraItem.id);
              if (fullExtra) {
                let d = extraItem.duration !== undefined ? extraItem.duration : fullExtra.duration;
                sumStandardDurations += d * extraItem.quantity;
              }
            }
          });
        }

        let customDurationToAssign = Math.max(0, orderTotalDuration - sumStandardDurations);

        // get the full service details
        if (orderData.data.services) {
          orderData.data.services.forEach((orderItem) => {
            const serviceItem = allServices!.find((sItem) => sItem.id === orderItem.id)
            if (serviceItem) {
              let itemDuration = orderItem.duration !== undefined ? orderItem.duration : serviceItem.duration;

              // If it's a custom service and duration evaluates to 0, use the leftover duration from the order times
              if (itemDuration === 0 && customDurationToAssign > 0 && orderItem.quantity > 0) {
                 itemDuration = customDurationToAssign / orderItem.quantity;
                 customDurationToAssign = 0; 
              }

              newCart.push({
                id: serviceItem.id,
                item: {
                  ...serviceItem, 
                  extra: false, 
                  parent_id: undefined,
                  duration: itemDuration,
                  price: orderItem.price !== undefined ? orderItem.price : serviceItem.price,
                  discount: orderItem.discount !== undefined ? orderItem.discount : serviceItem.discount,
                  price_with_discount: orderItem.price_with_discount !== undefined ? orderItem.price_with_discount : serviceItem.price_with_discount
                },
                quantity: orderItem.quantity,
                parent_id: undefined,
                is_upsell: false,
                type: serviceItem.type === "gift" ? "gift" : "typical"
              })
            }
          })
        }

        // get the full extra service details
        if (orderData.data.extra_services) {
          orderData.data.extra_services.forEach((extraItem) => {
            const parentService = allServices!.find(psItem => psItem.id === extraItem.parent_id)
            if (parentService) {
              const fullExtra = parentService.extra_services.find(e => e.id === extraItem.id)
              if (fullExtra) {
                newExtras.push({
                  id: fullExtra.id,
                  item: {
                    ...fullExtra,
                    parent_id: extraItem.parent_id,
                    extra: true,
                    duration: extraItem.duration !== undefined ? extraItem.duration : fullExtra.duration,
                    price: extraItem.price !== undefined ? extraItem.price : fullExtra.price,
                    discount: extraItem.discount !== undefined ? extraItem.discount : fullExtra.discount,
                    price_with_discount: (extraItem.price !== undefined ? extraItem.price : fullExtra.price) - (extraItem.discount !== undefined ? extraItem.discount : (fullExtra.discount || 0))
                  },
                  quantity: extraItem.quantity,
                  parent_id: extraItem.parent_id,
                  type: "extra"
                })
              }
            }
          })
        }

        setEntireCart(newCart, newExtras);

        setOrderDetails({
          client_firstname: orderData.data.customer.firstname || '',
          client_lastname: orderData.data.customer.lastname || '',
          client_email: orderData.data.customer.email || '',
          client_repeat_email: orderData.data.customer.email || '',
          client_mobile: orderData.data.customer.mobile || '',
          client_address: orderData.data.address?.address || '',
          latitude: orderData.data.address?.latitude ?? null,
          longitude: orderData.data.address?.longitude ?? null,
          eircode: orderData.data.address?.eircode || '',
          coupon_code: (orderData.data.customer as any).coupon_code || (orderData.data as any).coupon_code || '',
          time_start: orderData.data.time_start || null,
          cleaner_id: orderData.data.cleaner_id || null,
          random_cleaner: orderData.data.random_cleaner ? true : false,
          info: orderData.data.info || '',
        });

        setOrderInfo({
          orderId: orderData.data.id,
          time_start: orderData.data.time_start,
          cleaner_id: orderData.data.cleaner_id,
          clientSecret: orderData.data.client_secret,
          random_cleaner: orderData.data.random_cleaner ? true : false,
          status: orderData.data.status,
          reschedule_token: orderData.data.status === 'reserved' ? (token || undefined) : undefined
        });

        setPageCount(3);
      };

      hydrateCart();
    }
  }, [directRes.data, directUrl, setOrderDetails, setOrderInfo, setPageCount, setDirectPayment, setEntireCart]);

  return hasSettings ? (
    <CheckOutProvider
      directDetails={directRes.data}
      direct={orderDirect || directUrl}
      noCouponCheck={orderSuccess}
    >
      <Suspense fallback={<></>}>
        <div className="checkOut_wrapper">
          <BlackListWrapper
            listObjects={[
              {
                inView: timeError,
                text: t(
                  "Please select a different date for your cleaning. The selected date is no longer available"
                ),
                watch: timeError,
                nonClosable: true
              }
            ]}
          />
          <CheckOutHeader deactivate={deactivateBars} />

          {deactivateBars ? (
            (orderDirect || directUrl) ? (
              directRes.loading ? (
                t("Loading...")
              ) : directRes.data ? (
                <PaymentDirect directPaymentInfo={directRes.data} />
              ) : (
                <EmptyState
                  type="error"
                  icon={<CloseIcon />}
                  title={t("This payment link is no longer valid or the booking has been edited/cancelled.")}
                  action={
                    <Button onClick={() => navigate(baseName || "/", { replace: true })}>
                      {t("Return to Home" as any) || "Return to Home"}
                    </Button>
                  }
                />
              )
            ) : orderSuccess ? (
              <CheckOutSuccess customer_email={customer_email || ""} />
            ) : (
              <div style={{ textAlign: "center" }}>
                {directRes.loading ? t("Loading...") : ""}
              </div>
            )
          ) : (
            <>
              <CheckOutItemWrap page={0} pageCount={pageCount}>
                <CheckOutExtra settingRes={settingRes} />
              </CheckOutItemWrap>
              <CheckOutTime hide={pageCount !== 1} />
              <CheckOutItemWrap page={2} pageCount={pageCount}>
                <CheckOutLogin
                  onAuthenticated={() => setPageCount(only_gift({ cart, extras } as any) ? 4 : 3)}
                  initialEmail={orderDetails.client_email}
                />
              </CheckOutItemWrap>
              <CheckOutItemWrap page={3} pageCount={pageCount}>
                <CheckOutAddress />
              </CheckOutItemWrap>
              <CheckOutItemWrap page={4} pageCount={pageCount}>
                <Payment />
              </CheckOutItemWrap>
            </>
          )}
        </div>
      </Suspense>
    </CheckOutProvider>
  ) : (
    <></>
  );
};

const mapStateToProps = (state: stateType) => ({
  pageCount: state.commonReducer.pageCount,
  cart: state.cartReducer.cart,
  extras: state.cartReducer.extras,
  orderDetails: state.orderReducer.details,
  orderDirect: state.orderReducer.direct,
  orderInfo: state.orderReducer.orderInfo,
  orderSuccess: state.orderReducer.success,
  settings: state.settingsReducer,
  timeError: state.commonReloadReducer.timeError,
});

const mapDispatchToProps = (dispatch: any) => ({
  setPageCount: (data: number) => {
    dispatch(setPageCount(data));
  },
  setOrderDetails: (details: Partial<orderType>) => {
    dispatch(setOrderDetails(details) as any);
  },
  setDirectPayment: (data: boolean) => {
    dispatch(setDirectPayment(data));
  },
  setOrderInfo: (data: orderInfo) => {
    dispatch(setOrderInfo(data));
  },
  setPaymentSuccess: (data: boolean) => {
    dispatch(setPaymentSuccess(data));
  },
  setCheckOutError: (data: boolean) => {
    dispatch(setCheckOutError(data));
  },
  setTimeError: (data: boolean) => {
    dispatch(setTimeError(data));
  },
  setEntireCart: (cart: cartItem[], extras: extrasCartItem[]) => {
    dispatch(setEntireCart({cart, extras}));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(CheckOut);

const CheckOutItemWrap = ({
  page,
  pageCount,
  children
}: {
  page: number;
  pageCount: proceed_val_type;
  children: ReactElement;
}) => {
  return page === pageCount ? <>{children}</> : <div></div>;
};

export const CheckOutSuccess = ({
  customer_email
}: {
  customer_email: string;
}) => {
  const { t, locale } = useContext(LocaleContext);

  return (
    <div className=" checkout_left check_out_successful_wrapper center fdCol">
      <div className="top center">
        <span className="suc_logo">
          <Tick className="img_div_contain" />
        </span>
        <span>
          {t("Thank you for your booking! Details have been emailed to you.")}
        </span>
      </div>
    </div>
  );
};

export const BlackListWrapper = ({
  listObjects
}: {
  listObjects: {
    text: string;
    inView: boolean;
    watch: boolean;
    nonClosable?: boolean;
  }[];
}) => {
  const show = listObjects?.some((item) => item.inView && item.text);
  return show ? (
    <div className="blacklist_wrapper">
      {listObjects.map(({ text, inView, watch, nonClosable }, n) => (
        <BlackList
          key={text + n}
          text={text}
          inView={inView}
          watch={watch}
          nonClosable={nonClosable}
        />
      ))}
    </div>
  ) : (
    <></>
  );
};

const BlackList = ({
  text,
  inView,
  watch,
  nonClosable
}: {
  text: string; // Text to display as notification
  inView: boolean; // Indicates whether or not to show indicator
  watch: boolean; // Value to be put in dependency array of useEffect
  nonClosable?: boolean; // whether or not a close button is shown. This also prevent spontaneous hiding on watch change
}) => {
  const [show, setShow] = useState(true);
  const [initialMount, setInitialMount] = useState(true);

  useEffect(() => {
    if (inView && !nonClosable) {
      setShow(watch);

      if (initialMount) {
        setInitialMount(false);
      }
    }
  }, [watch]);

  return inView && show ? (
    <div className="blacklist_error_wrapper">
      <div className="blacklist_error_container">{text}</div>
      <div className="blacklist_error_btn_close center">
        {!nonClosable && (
          <CloseIcon
            className="header_menu_close_icon img_div_contain"
            onClick={() => setShow(false)}
          />
        )}
      </div>
    </div>
  ) : (
    <></>
  );
};

/**
 * getDisabledDates: /api/get-disabled-days (POST)
 * checkAddress: /api/address/check (POST)
 * get_cleanerTime: /api/cleaners (POST)
 * getOrderPrice: /api/orders/calculate-amount (POST)
 * getDepositAmount: /api/orders/calculate-deposit (POST)
 */
